import React from "react"
import { useTranslation } from "react-i18next"
import {
  faCircleDollarToSlot,
  faClock,
  faClose,
  faKey,
  faLink,
  faMessage,
  faRobot,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import Button, { ButtonIcon } from "../../../../components/Button/Button"
import InputField from "../../../../components/InputField/InputField"
import { SimpleToggle } from "../../../../components/SimpleToggle/SimpleToggle"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"
import type { IWhatsAppAiMessageSettings } from "../../../../Objects/WhatsAppAutomation/WhatsAppAiMessageSettings"

type BotHelpType =
  | "enabled"
  | "endpoint"
  | "apiKey"
  | "model"
  | "displayName"
  | "personality"
  | "orders"
  | "handoff"
  | "handoffTimeout"

type Props = {
  isOpen: boolean
  settings: IWhatsAppAiMessageSettings
  onClose: () => void
  onChange: (settings: IWhatsAppAiMessageSettings) => void
  onSave: () => void
}

export const WhatsAppBotSettingsPopup: React.FC<Props> = ({ isOpen, settings, onClose, onChange, onSave }) => {
  const { t } = useTranslation(["pages", "buttons", "helpPopup"])

  if (!isOpen) {
    return null
  }

  const patch = (next: Partial<IWhatsAppAiMessageSettings>) => onChange({ ...settings, ...next })

  const applyOpenAiPreset = () =>
    patch({
      aiEndpointUrl: "https://api.openai.com/v1/chat/completions",
      aiModel: "gpt-4o-mini",
    })

  const applyClaudePreset = () =>
    patch({
      aiEndpointUrl: "https://api.anthropic.com/v1/messages",
      aiModel: "claude-3-5-haiku-latest",
    })

  const help = (type: BotHelpType) => ({
    title: t(`helpPopup:sections.whatsAppBotSettings.${type}.title`),
    description: t(`helpPopup:sections.whatsAppBotSettings.${type}.description`),
    bullets: t(`helpPopup:sections.whatsAppBotSettings.${type}.bullets`, { returnObjects: true }) as string[],
  })

  const patchHandoffTimeout = (value: string) => {
    const parsed = Number(value)

    patch({ humanHandoffTimeoutMinutes: Number.isFinite(parsed) ? parsed : 10 })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              iconColor={THEME.colors.iconBGColor}
              title={t("label_whatsapp_bot_settings_title")}
              icon={faRobot}
            >
              <div className="flex items-center gap-2">
                <PopupHelpButton
                  variant="global"
                  title={t("helpPopup:global.whatsAppBotSettings.title")}
                  description={t("helpPopup:global.whatsAppBotSettings.description")}
                  steps={t("helpPopup:global.whatsAppBotSettings.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.whatsAppBotSettings.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="max-h-[72vh] overflow-x-hidden overflow-y-auto p-2">
              <div className="grid min-w-0 gap-3">
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <section className="rounded-lg border-2 border-emerald-100 bg-emerald-50 p-3">
                    <div className="flex flex-col items-center gap-3 text-justify sm:flex-row sm:items-start sm:text-left">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-emerald-700 sm:mt-0.5">
                        <FontAwesomeIcon icon={faRobot} />
                      </span>

                      <div className="min-w-0">
                        <strong className="block text-sm text-emerald-900">
                          {t("label_whatsapp_bot_settings_title")}
                        </strong>

                        <p className="mt-1 text-xs font-medium leading-relaxed text-emerald-800">
                          {t("label_whatsapp_bot_settings_description")}
                        </p>
                      </div>
                    </div>
                  </section>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <section className="rounded-lg border-2 border-amber-100 bg-amber-50 p-3">
                    <div className="flex flex-col items-center gap-3 text-justify sm:flex-row sm:items-start sm:text-left">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-amber-700 sm:mt-0.5">
                        <FontAwesomeIcon icon={faCircleDollarToSlot} />
                      </span>

                      <div className="min-w-0">
                        <strong className="block text-sm text-amber-950">{t("label_whatsapp_bot_cost_title")}</strong>

                        <p className="mt-1 text-xs font-medium leading-relaxed text-amber-900">
                          {t("label_whatsapp_bot_cost_description")}
                        </p>
                      </div>
                    </div>
                  </section>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s" delay="0.08s">
                  <section className="rounded-lg border-2 border-sky-100 bg-sky-50 p-3">
                    <div className="flex flex-col items-center gap-3 text-justify sm:flex-row sm:items-start sm:text-left">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-sky-700 sm:mt-0.5">
                        <FontAwesomeIcon icon={faLink} />
                      </span>

                      <div className="min-w-0">
                        <strong className="block text-sm text-sky-900">{t("label_whatsapp_bot_presets_title")}</strong>

                        <p className="mt-1 text-xs leading-relaxed text-sky-800">
                          {t("label_whatsapp_bot_presets_description")}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 grid gap-2 sm:flex sm:flex-wrap">
                      <button
                        type="button"
                        onClick={applyOpenAiPreset}
                        className="rounded-lg border border-sky-300 bg-white px-3 py-2 text-xs font-bold text-sky-700 transition hover:bg-sky-100"
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.button.applyOpenAiPreset"
                      >
                        {t("label_whatsapp_bot_preset_openai")}
                      </button>

                      <button
                        type="button"
                        onClick={applyClaudePreset}
                        className="rounded-lg border border-sky-300 bg-white px-3 py-2 text-xs font-bold text-sky-700 transition hover:bg-sky-100"
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.button.applyClaudePreset"
                      >
                        {t("label_whatsapp_bot_preset_claude")}
                      </button>
                    </div>
                  </section>
                </Animate>

                <section className="grid min-w-0  md:grid-cols-2">
                  <FieldHelpWrapper helpProps={help("enabled")}>
                    <SimpleToggle
                      title={t("label_whatsapp_bot_enabled")}
                      value={settings.isBotEnabled}
                      onChange={(value) => patch({ isBotEnabled: value })}
                      animationDuration="1.1s"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.toggle.whatsappBotEnabled"
                    />
                  </FieldHelpWrapper>

                  <FieldHelpWrapper helpProps={help("orders")}>
                    <SimpleToggle
                      title={t("label_whatsapp_bot_can_guide_orders")}
                      value={settings.canGuideOrders}
                      onChange={(value) => patch({ canGuideOrders: value })}
                      animationDuration="1.1s"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.toggle.whatsappBotCanGuideOrders"
                    />
                  </FieldHelpWrapper>

                  <FieldHelpWrapper helpProps={help("endpoint")}>
                    <InputField
                      type="link"
                      icon={faLink}
                      placeholder={t("label_whatsapp_bot_ai_endpoint")}
                      value={settings.aiEndpointUrl}
                      onChange={(event) => patch({ aiEndpointUrl: event.target.value })}
                      animationDuration="1.1s"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.input.whatsappBotAiEndpoint"
                    />
                  </FieldHelpWrapper>

                  <FieldHelpWrapper helpProps={help("apiKey")}>
                    <InputField
                      type="password"
                      icon={faKey}
                      placeholder={t("label_whatsapp_bot_ai_key")}
                      value={settings.aiApiKey}
                      onChange={(event) => patch({ aiApiKey: event.target.value })}
                      animationDuration="1.1s"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.input.whatsappBotAiKey"
                    />
                  </FieldHelpWrapper>

                  <FieldHelpWrapper helpProps={help("model")}>
                    <InputField
                      type="text"
                      icon={faRobot}
                      placeholder={t("label_whatsapp_bot_ai_model")}
                      value={settings.aiModel}
                      onChange={(event) => patch({ aiModel: event.target.value })}
                      animationDuration="1.1s"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.input.whatsappBotAiModel"
                    />
                  </FieldHelpWrapper>

                  <FieldHelpWrapper helpProps={help("displayName")}>
                    <InputField
                      type="text"
                      icon={faUserTie}
                      placeholder={t("label_whatsapp_bot_display_name")}
                      value={settings.botDisplayName ?? ""}
                      onChange={(event) => patch({ botDisplayName: event.target.value })}
                      animationDuration="1.1s"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.input.whatsappBotDisplayName"
                    />
                  </FieldHelpWrapper>

                  <FieldHelpWrapper helpProps={help("handoff")}>
                    <InputField
                      type="text"
                      icon={faMessage}
                      placeholder={t("label_whatsapp_bot_handoff_keywords")}
                      value={settings.humanHandoffKeywords}
                      onChange={(event) => patch({ humanHandoffKeywords: event.target.value })}
                      animationDuration="1.1s"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.input.whatsappBotHandoffKeywords"
                    />
                  </FieldHelpWrapper>

                  <FieldHelpWrapper helpProps={help("handoffTimeout")}>
                    <InputField
                      type="number"
                      icon={faClock}
                      placeholder={t("label_whatsapp_bot_handoff_timeout")}
                      value={String(settings.humanHandoffTimeoutMinutes ?? 10)}
                      onChange={(event) => patchHandoffTimeout(event.target.value)}
                      animationDuration="1.1s"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.input.whatsappBotHandoffTimeout"
                    />
                  </FieldHelpWrapper>
                </section>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="min-w-0 rounded-lg border-2 border-slate-200 bg-white p-3">
                    <div className="mb-2 flex min-w-0 items-center gap-2">
                      <label className="min-w-0 flex-1 truncate text-sm font-medium text-gray-600">
                        {t("label_whatsapp_bot_personality")}
                      </label>

                      <div className="shrink-0">
                        <PopupHelpButton {...help("personality")} />
                      </div>
                    </div>

                    <textarea
                      className="w-full max-w-full rounded-lg border-2 border-slate-200 bg-white p-2 text-xs text-slate-700 outline-none transition-all duration-300 focus:border-emerald-400"
                      rows={5}
                      maxLength={2000}
                      value={settings.botPersonality}
                      onChange={(event) => patch({ botPersonality: event.target.value })}
                      placeholder={t("label_whatsapp_bot_personality")}
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.input.whatsappBotPersonality"
                    />
                    <p className="mt-1 text-right text-[11px] text-slate-400">{settings.botPersonality.length}/2000</p>
                  </div>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="flex justify-end">
                    <Button
                      bgColor={THEME.colors.buttonBGSaveColor}
                      label={t("buttons:button_save")}
                      onClick={onSave}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppBotSettingsPopup.button.save"
                    />
                  </div>
                </Animate>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}

const FieldHelpWrapper: React.FC<{
  children: React.ReactNode
  helpProps: React.ComponentProps<typeof PopupHelpButton>
}> = ({ children, helpProps }) => (
  <Animate type={AnimationTypes.slideUpIn} duration="1.15s">
    <div className="flex min-w-0 min-h-[58px] items-center gap-2">
      <div className="min-w-0 p-3 flex-1 overflow-hidden">{children}</div>

      <div className="shrink-0 self-center">
        <PopupHelpButton {...helpProps} />
      </div>
    </div>
  </Animate>
)
